import './applyVehicleConditionPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { VehicleCondition } from '@thesavingsgroup/enums/VehicleCondition';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/shared_new/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/shared_new/RadioGroup/types/RadioOptionClickEvent';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { isValidClickEvent } from '../../../utils/component/isValidClickEvent';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const { vehicleCondition, setVehicleCondition } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      vehicleConditionValue: vehicleCondition,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<VehicleCondition>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const vehicleConditionValue = event.target.value;

    setIsInitialSelection(
      isEmpty(vehicleCondition) && isValidClickEvent(event),
    );

    setVehicleCondition(vehicleConditionValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { vehicleConditionValue } = values;

    if (vehicleConditionValue) {
      navigate(next());
    }
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <div className="apply-vehicle-condition-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applyVehicleCondition.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applyVehicleCondition.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.applyVehicleCondition.header.drawerButton
              }
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applyVehicleCondition.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applyVehicleCondition.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applyVehicleCondition.form.h1}>
                  {t('ApplyVehicleConditionPage.title')}
                </h1>
                <h2 data-testid={locator.pages.applyVehicleCondition.form.h2}>
                  {t('ApplyVehicleConditionPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioGroup
                    defaultValue={vehicleCondition}
                    labelPlacement="below"
                    name="vehicleConditionValue"
                    options={[
                      {
                        label: t('ApplyVehicleConditionPage.new'),
                        value: VehicleCondition.NEW,
                        testId:
                          locator.pages.applyVehicleCondition.form.radioOption1,
                      },
                      {
                        label: t('ApplyVehicleConditionPage.used'),
                        value: VehicleCondition.USED,
                        testId:
                          locator.pages.applyVehicleCondition.form.radioOption2,
                      },
                    ]}
                    register={register}
                    testId={
                      locator.pages.applyVehicleCondition.form.radioGroupRoot
                    }
                    variant="inline"
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.applyVehicleCondition.form.submitButton
                    }
                    disabled={isEmpty(vehicleCondition)}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplyVehicleConditionPage.continue')}
                  </LoadingButton>
                </div>
              </div>
              <footer
                data-testid={locator.pages.applyVehicleCondition.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyVehicleConditionPage';
